import { useGetPresentersList } from '@/hooks';
import { isUserAvatar, isUserClipAvatar } from '@/pages/avatar';
import { GetPresentersByStatus } from '@/services/api';
import { groupBy } from '@/utils/utils';
import { ImageType, isPremiumPresenter, isV2Presenter, Presenter, PresenterUsecase } from '@d-id/types';
import { useMemo } from 'react';

const SP_AGENT_TO_USE = 'with_background';
const DEPRECATED_AVATARS = new Map<string, string>([
    ['v2_public_amy@seiu0o2gby', '2025-06-01'],
    ['amy-jcwCkr1grs', '2025-06-01'],
    ['amy-sEIU0O2gBy', '2025-06-01'],
    ['amy-FLZ1USJl7m', '2025-06-01'],
    ['amy-Aq6OmGZnMt', '2025-06-01'],
]);

export const useAllAvatars = (
    usecase: PresenterUsecase = PresenterUsecase.Video,
    status?: GetPresentersByStatus,
    includeNotReady: boolean = false
) => {
    const consolidatedUseCase = [PresenterUsecase.Agent, PresenterUsecase.AgentV2].includes(usecase) ? PresenterUsecase.Agent : usecase;
    const { data: avatars = [], isLoading: avatarsLoading } = useGetPresentersList({
        usecase: consolidatedUseCase,
        status,
        includeNotReady,
    });
    const { data: userAvatars = [], isLoading: userAvatarsLoading } = useGetPresentersList({
        includeNotReady,
        presenterType: ImageType.UserPresenters,
        status,
    });

    const spV2Representatives = groupBy(
        avatars.filter(avatar => avatar.poster?.includes('DefaultPresenters') && avatar.poster?.includes(SP_AGENT_TO_USE)),
        avatar => avatar.id
    );

    const allAvatars = useMemo(() => {
        const map = new Map<string, Presenter>();
        userAvatars
            .concat(avatars)
            .filter(avatar => !avatar.id?.includes('-removed_bg'))
            .filter(
                avatar =>
                    !(usecase === PresenterUsecase.Video) ||
                    !isPremiumPresenter(avatar) ||
                    isUserClipAvatar(avatar) ||
                    isV2Presenter(avatar.id)
            )
            .forEach(avatar => {
                if (usecase === PresenterUsecase.AgentV2 && avatar.poster?.includes('DefaultPresenters')) {
                    map.set(avatar.id, spV2Representatives[avatar.id]?.[0] ?? avatar);
                } else {
                    map.set(avatar.id, {
                        ...avatar,
                        deprecationDate: DEPRECATED_AVATARS.get(avatar.id),
                    } as Presenter);
                }
            });
        return Array.from(map.values());
    }, [avatars, userAvatars]);

    const publicAvatars = useMemo(() => allAvatars.filter(a => !isUserAvatar(a) && !isUserClipAvatar(a)), [allAvatars]);

    const filteredUserAvatars = useMemo(() => allAvatars.filter(a => isUserAvatar(a) || isUserClipAvatar(a)), [allAvatars]);

    const hasSkeleton = useMemo(() => publicAvatars.some(a => a.presenterType === ImageType.Skeleton), [publicAvatars]);
    const isLoading = avatarsLoading || userAvatarsLoading || hasSkeleton;

    return {
        isLoading,
        allAvatars,
        userAvatars: filteredUserAvatars,
        publicAvatars,
    };
};
